import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBox, FaCheck, FaEye, FaFilter, FaSearch, FaTimes } from 'react-icons/fa';
import * as clientCatalogService from '../../services/clientCatalog.service';
import * as clientService from '../../services/client.service';
import PageHeader from '../../components/PageHeader/PageHeader';
import { useAuth } from '../../context/AuthContext';
import '../ClientCatalog/ClientCatalog.css';
import { getChangedTearSheetFields } from './authorizationUtils';

const INITIAL_MODAL_STATE = {
  open: false,
  item: null,
  previous: null,
  action: 'approve',
};

const ProductAuthorization = () => {
  const { hasModuleAccess } = useAuth();
  const canAuthorize = hasModuleAccess('authorize_action');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(INITIAL_MODAL_STATE);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    const loadClients = async () => {
      try {
        const response = await clientService.getClientList();
        const clientList = Array.isArray(response?.data) ? response.data : [];
        setClients(clientList);
      } catch (error) {
        console.error('Failed to load clients for authorization page', error);
      }
    };

    loadClients();
  }, []);

  const loadCatalogs = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }

    try {
      const catalogPromises = clients.map(async (client) => {
        const clientCode = client?.clientId || client?.client_id;
        if (!clientCode) {
          return [];
        }

        const catalog = await clientCatalogService.getClientCatalog(clientCode);
        return (catalog || []).map((item) => ({
          ...item,
          clientId: clientCode,
          clientName: client?.name || client?.clientName || clientCode,
          status: (item?.tearSheetStatus || 'unapproved').toLowerCase(),
          remarks: item?.remark || item?.remarks || '—',
          updatedBy: item?.updatedBy || item?.updated_by || 'Admin',
          image: item?.images?.[0]?.path || '',
          // Baseline captured by the backend at last approval; used to diff changes in the Review modal.
          approvedSnapshot: item?.approvedSnapshot || null,
          version: item?.version || 1,
        }));
      });

      const results = await Promise.all(catalogPromises);
      setRows(results.flat());
    } catch (error) {
      console.error('Failed to load authorization rows', error);
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  }, [clients]);

  useEffect(() => {
    if (clients.length > 0) {
      loadCatalogs(true);
    } else {
      setRows([]);
    }
  }, [clients, loadCatalogs]);

  useEffect(() => {
    const handleFocus = () => {
      if (clients.length > 0) {
        loadCatalogs(false);
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [clients, loadCatalogs]);

  const handleReview = (row, action) => {
    setModal({
      open: true,
      item: row,
      previous: row.approvedSnapshot,
      action,
    });
  };

  const handleApproveOrReject = async (decision) => {
    if (!modal.item?.uid || !modal.item?.clientId) {
      return;
    }

    setSubmitting(true);
    try {
      const nextStatus = decision === 'approve' ? 'approved' : 'unapproved';

      const updated = await clientCatalogService.updateClientCatalogAuthorization(
        modal.item.clientId,
        modal.item.uid,
        nextStatus,
      );

      setRows((prev) =>
        prev.map((row) => {
          if (row.uid !== modal.item.uid || row.clientId !== modal.item.clientId) {
            return row;
          }

          return {
            ...row,
            status: nextStatus,
            updatedBy: updated?.updatedBy || row.updatedBy,
            // Approving refreshes the baseline; unapproving keeps the previous approved baseline.
            approvedSnapshot: updated?.approvedSnapshot ?? row.approvedSnapshot,
            version: updated?.version ?? row.version,
          };
        }),
      );

      setModal(INITIAL_MODAL_STATE);
    } catch (error) {
      console.error('Failed to change authorization status', error);
      alert('Failed to update authorization status');
    } finally {
      setSubmitting(false);
    }
  };

  const rowsKeyed = useMemo(
    () =>
      rows.map((row) => ({
        ...row,
        key: `${row.clientId}:${row.uid}`,
      })),
    [rows],
  );

  const visibleRows = useMemo(() => {
    const searchValue = searchTerm.trim().toLowerCase();

    return rowsKeyed.filter((row) => {
      const matchesStatus = statusFilter === 'all' || row.status === statusFilter;
      if (!matchesStatus) {
        return false;
      }

      if (!searchValue) {
        return true;
      }

      const haystack = [
        row.clientName,
        row.pCode,
        row.itemNo,
        row.productName,
        row.remarks,
        row.updatedBy,
        row.status,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return haystack.includes(searchValue);
    });
  }, [rowsKeyed, searchTerm, statusFilter]);

  return (
    <div className="client-catalog-container authorization-page" style={{ minHeight: '100vh' }}>
      <PageHeader
        icon={<FaBox size={22} />}
        title="Product Authorization"
        subtitle="Review catalog items, approve or reject updates, and track changed fields"
      />

      <div className="client-catalog-main">
        <section className="catalog-content-area-full">
          <div className="authorization-summary-row">
            <div className="authorization-summary-card">
              <span>Total catalog rows</span>
              <strong>{rows.length}</strong>
            </div>
            <div className="authorization-summary-card">
              <span>Approved</span>
              <strong>{rows.filter((row) => row.status === 'approved').length}</strong>
            </div>
            <div className="authorization-summary-card">
              <span>Unapproved</span>
              <strong>{rows.filter((row) => row.status === 'unapproved').length}</strong>
            </div>
          </div>

          <div className="authorization-toolbar">
            <label className="authorization-search">
              <FaSearch className="authorization-search-icon" />
              <input
                type="text"
                placeholder="Search buyer, P code, item no., product, remarks..."
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
              />
            </label>

            <label className="authorization-filter">
              <FaFilter className="authorization-search-icon" />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                <option value="all">All statuses</option>
                <option value="approved">Approved</option>
                <option value="unapproved">Unapproved</option>
              </select>
            </label>
          </div>

          {loading ? (
            <div className="empty-state authorization-empty-state">
              <p>Loading products...</p>
            </div>
          ) : visibleRows.length === 0 ? (
            <div className="empty-state authorization-empty-state">
              <FaBox size={48} className="text-muted mb-3" />
              <h5>No matching catalog items</h5>
              <p className="text-muted">Try adjusting the search term or status filter.</p>
            </div>
          ) : (
            <div className="authorization-table-card">
              <div className="table-responsive">
                <table className="table authorization-table align-middle mb-0">
                  <thead>
                    <tr>
                      <th>SR.NO</th>
                      <th>Buyer Name</th>
                      <th>P code</th>
                      <th>Item No.</th>
                      <th>Product name</th>
                      <th>Primary image</th>
                      <th>Remarks</th>
                      <th>Status</th>
                      <th>Updated By</th>
                      <th>Authorize action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleRows.map((row, index) => (
                      <tr key={row.key}>
                        <td>{index + 1}</td>
                        <td>{row.clientName}</td>
                        <td>{row.pCode || '—'}</td>
                        <td>{row.itemNo || '—'}</td>
                        <td>
                          <div className="authorization-product-cell">
                            <span>{row.productName || '—'}</span>
                            {row.version > 1 && row.status === 'unapproved' && (
                              <span className="authorization-version-tag" title="Updated after approval — pending re-approval">
                                New version · v{row.version}
                              </span>
                            )}
                            {row.version > 1 && row.status === 'approved' && (
                              <span className="authorization-version-tag muted" title="Current approved revision">
                                v{row.version}
                              </span>
                            )}
                          </div>
                        </td>
                        <td>
                          {row.image ? (
                            <img
                              src={row.image}
                              alt={row.productName || row.itemNo}
                              style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8 }}
                            />
                          ) : (
                            <span className="text-muted">No image</span>
                          )}
                        </td>
                        <td>{row.remarks || '—'}</td>
                        <td>
                          <span className={`authorization-status-pill ${row.status === 'approved' ? 'approved' : 'unapproved'}`}>
                            {row.status === 'approved' ? 'Approved' : 'Unapproved'}
                          </span>
                        </td>
                        <td>{row.updatedBy || '—'}</td>
                        <td>
                          <div className="authorization-action-group">
                            {canAuthorize &&
                              (row.status === 'approved' ? (
                                <button className="btn btn-sm btn-outline-danger" onClick={() => handleReview(row, 'unapprove')}>
                                  <FaTimes className="me-1" /> Unapprove
                                </button>
                              ) : (
                                <button className="btn btn-sm btn-outline-success" onClick={() => handleReview(row, 'approve')}>
                                  <FaCheck className="me-1" /> Approve
                                </button>
                              ))}
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => handleReview(row, 'review')}>
                              <FaEye className="me-1" /> Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      </div>

      {modal.open && modal.item && (
        <div className="modal-backdrop" role="dialog" aria-modal="true">
          <div className="import-modal" style={{ maxWidth: 900 }}>
            <div className="modal-header">
              <div>
                <h5>{modal.action === 'approve' ? 'Approve product' : modal.action === 'unapprove' ? 'Unapprove product' : 'Review changes'}</h5>
                <p className="text-muted mb-0">{modal.item.clientName} • {modal.item.pCode || modal.item.itemNo}</p>
              </div>
              <button className="btn-close-client" onClick={() => setModal(INITIAL_MODAL_STATE)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body" style={{ padding: '1.5rem' }}>
              <div className="row g-3">
                <div className="col-md-6">
                  <h6>Latest details</h6>
                  <ul className="list-group">
                    <li className="list-group-item">
                      <strong>Buyer:</strong> {modal.item.clientName}
                    </li>
                    <li className="list-group-item">
                      <strong>P code:</strong> {modal.item.pCode || '—'}
                    </li>
                    <li className="list-group-item">
                      <strong>Item No.:</strong> {modal.item.itemNo || '—'}
                    </li>
                    <li className="list-group-item">
                      <strong>Product name:</strong> {modal.item.productName || '—'}
                    </li>
                    <li className="list-group-item">
                      <strong>Remarks:</strong> {modal.item.remarks || '—'}
                    </li>
                    <li className="list-group-item">
                      <strong>Status:</strong> {modal.item.status}
                    </li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <h6>Changes since last approval</h6>
                  <div className="list-group">
                    {!modal.previous ? (
                      <div className="list-group-item text-muted">
                        No approved baseline yet — this product has not been approved.
                      </div>
                    ) : (
                      (() => {
                        const changes = getChangedTearSheetFields(modal.item, modal.previous);
                        if (changes.length === 0) {
                          return (
                            <div className="list-group-item text-muted">
                              No field changes since the last approval.
                            </div>
                          );
                        }
                        return changes.map((field) => (
                          <div key={field.key} className="list-group-item">
                            <div className="fw-semibold">{field.label}</div>
                            <div className="small text-danger">Old: {field.oldValue}</div>
                            <div className="small text-success">New: {field.newValue}</div>
                          </div>
                        ));
                      })()
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline-secondary" onClick={() => setModal(INITIAL_MODAL_STATE)}>
                Cancel
              </button>
              {(modal.action === 'approve' || modal.action === 'unapprove') && (
                <button
                  className={modal.action === 'approve' ? 'btn btn-success' : 'btn btn-danger'}
                  onClick={() => handleApproveOrReject(modal.action)}
                  disabled={submitting}
                >
                  {submitting ? 'Updating...' : modal.action === 'approve' ? 'Approve' : 'Unapprove'}
                </button>
              )}
              {modal.action === 'review' && (
                <button className="btn btn-primary" onClick={() => setModal(INITIAL_MODAL_STATE)}>
                  Close
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductAuthorization;